import React from 'react';
import { Trans } from 'react-i18next';

import { LOADING_OPTIONS, Modal, Text } from '@components';
import { Box } from '@core';
import { DealsCouponErrorModal } from '@containers/modals';
import { META_IMAGE, PRICING_LINK } from '@shared/constants/constants';
import { SERVER_COUNT } from '@shared/constants/server-locations';
import { FEATURES_TAB_DATA } from '@shared/constants/features';
import { GA4_EVENTS } from '@shared/constants/google';
import { FadeInTitle, TitleWithIncrementalDiscount } from '@shared/deals-helpers';
import { getFetchedImage } from '@shared/image-helpers';
import { ONE_MONTH_PLAN } from '@shared/constants/checkout';

// TODO, this one is temporary for DealsHeader in storybook, find a better solution for stories
import dealsHeaderImage from '@images/deals/deals-header/deals-header.png';

// Compatible devices
import Android from '@images/devices/android-dark.svg';
import AndroidLight from '@images/devices/android-light.svg';
import iOS from '@images/devices/iOS-dark.svg';
import iOSLight from '@images/devices/iOS-light.svg';
import MacOS from '@images/devices/macOS-dark.svg';
import MacOSLight from '@images/devices/macOS-light.svg';
import Windows from '@images/devices/windows-dark.svg';
import WindowsLight from '@images/devices/windows-light.svg';
import AndroidTv from '@images/devices/android-tv-dark.svg';
import AndroidTvLight from '@images/devices/android-tv-light.svg';
import FireTv from '@images/devices/fire-tv-dark.svg';
import FireTvLight from '@images/devices/fire-tv-light.svg';
import Linux from '@images/devices/linux-dark.svg';
import LinuxLight from '@images/devices/linux-light.svg';

const getImageByVariant = ({ variant, images }) => images[variant];

export const getCTAButtonText = (t) => t('common:actions.getTheDeal');

const getReviews = ({ t, images }) => ({
  items: [
    {
      text: '“Speeds — Even Faster Than My Base Speed”',
      image: getFetchedImage(images, 'deals/review-logos/vpn-mentor.png'),
      name: 'VPN Mentor',
      rating: '8/10',
      link: 'https://www.vpnmentor.com/reviews/atlas-vpn/',
    },
    {
      text: '“With premium service the experience gets even better.”',
      image: getFetchedImage(images, 'deals/review-logos/top10-vpn.png'),
      name: 'Top 10 VPN',
      rating: '9,1/10',
      link: 'https://www.top10vpn.com/reviews/atlas-vpn/',
    },
    {
      text: '“The applications offered by Atlas VPN are easy to use and extremely intuitive.”',
      image: getFetchedImage(images, 'deals/review-logos/cybernews.png'),
      name: 'Cybernews',
      rating: '4.6/5',
      link: '', // empty because unused
    },
    {
      text: '“A fast and affordable VPN with a 30-day money-back guarantee.”',
      image: getFetchedImage(images, 'deals/review-logos/the-best-vpn-deals.png'),
      name: 'The Best VPN Deals',
      rating: '4,4/5',
      link: 'https://thebestvpndeals.com/reviews/atlasvpn/',
    },
    {
      text: '“Atlas VPN is reliable and excellent choice in the market”',
      image: getFetchedImage(images, 'deals/review-logos/vpn-pro.png'),
      name: 'VPN Pro',
      rating: '7,5/10',
      link: 'https://vpnpro.com/vpn-reviews/atlas-vpn-review/',
    },
    {
      text: "“Atlas competes with the best of VPN's and come out unscathed”",
      image: getFetchedImage(images, 'deals/review-logos/vpn-alert.png'),
      name: 'VPN Alert',
      rating: '7,9/10',
      link: 'https://vpnalert.com/best-virtual-private-network/reviews/atlas-vpn/',
    },
  ],
  button: {
    link: PRICING_LINK,
    title: getCTAButtonText(t),
    buttonProps: {
      customEvent: { eventName: GA4_EVENTS.footerCtaClick },
    },
  },
});

export const PAGE_DATA = ({ variant = 'dark', images }) => ({
  metaData: (t) => ({
    title: t('deals:metaData.title'),
    description: t('deals:metaData.description'),
    image: META_IMAGE,
  }),
  headerCta: {
    text: ({
      discountPercentage,
      pricePerMonth,
      subscriptionPeriod,
      isLoading,
      extraTime,
      t,
      currency,
    }) => ({
      title: discountPercentage ? (
        <TitleWithIncrementalDiscount
          finalDiscountPercentage={!isLoading && discountPercentage}
          i18nKey="campaigns:spring.title"
          components={[
            <FadeInTitle color="danger" as="span" fontFamily="inherit" whiteSpace="nowrap" key={0}>
              <Box display="inline-flex" justifyContent="center" minWidth={{ _: 75, lg: 112 }} />
            </FadeInTitle>,
          ]}
        />
      ) : (
        t('deals:header.getDeal')
      ),
      subtitle: (
        <Trans
          i18nKey={'deals:header.description'}
          values={{
            planDetail: pricePerMonth
              ? t(
                  [
                    `deals:header.planDeal.${subscriptionPeriod}`,
                    '' /*setting default value to '' in case there is no translation for subscriptionPeriod*/,
                  ],
                  {
                    currency,
                    pricePerMonth,
                  }
                )
              : null,
            extraComment: pricePerMonth && extraTime && t(`plans:monthsExtra.${extraTime}`),
          }}
          components={[<Text.Subheading2 as="span" key={0} color="danger" display="inline-flex" />]}
        />
      ),
      button: {
        title: t('deals:common.grabDeal'),
        link: PRICING_LINK,
        customEvent: { eventName: GA4_EVENTS.ctaClick },
      },
      description: [
        subscriptionPeriod === ONE_MONTH_PLAN
          ? t('common:14dayMoneyBackGuarantee')
          : t('common:30dayMoneyBackGuarantee'),
      ],
    }),

    image: () => ({
      mobile: {
        src: images
          ? getFetchedImage(images, 'deals/deals-header/deals-header-bf23.png')
          : dealsHeaderImage,
        alt: 'deals header',
        position: {
          height: 344,
        },
        loading: LOADING_OPTIONS.eager,
      },
      tablet: {
        src: images
          ? getFetchedImage(images, 'deals/deals-header/deals-header-bf23.png')
          : dealsHeaderImage,
        alt: 'deals header',
        position: {
          height: 540,
        },
        loading: LOADING_OPTIONS.eager,
      },
      desktop: {
        src: images
          ? getFetchedImage(images, 'deals/black-friday-animation/deals-black-friday-header.png')
          : dealsHeaderImage,
        alt: 'deals header',
        position: {
          height: { lg: 360, xl: 548 },
          width: 556,
        },
        loading: LOADING_OPTIONS.eager,
      },
      isImageAnimated: true,
    }),
  },
  newsOutlets: (t) => ({
    title: t('deals:news.title'),
  }),
  timerBar: ({ t }) => ({
    button: {
      title: t('common:actions.getAtlasVpn'),
      link: PRICING_LINK,
      buttonProps: {
        customEvent: { eventName: GA4_EVENTS.stickyHeaderCtaClick },
      },
    },
  }),
  comparison: ({ t }) => ({
    title: t('deals:comparison.title'),
    description: t('deals:comparison.description'),
  }),
  featuresTabs: ({ t, variant }) => ({
    tabsData: FEATURES_TAB_DATA({ t, variant, images }),
  }),
  compatibleDevices: (t) => ({
    title: t('deals:compatibleDevices.title'),
    cards: [
      {
        image: getImageByVariant({
          variant,
          images: { dark: Windows, light: WindowsLight },
        }),
        title: t('common:platforms.windows'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: MacOS, light: MacOSLight },
        }),
        title: t('common:platforms.macOS'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: Linux, light: LinuxLight },
        }),
        title: t('common:platforms.linux'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: Android, light: AndroidLight },
        }),
        title: t('common:platforms.android'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: iOS, light: iOSLight },
        }),
        title: t('common:platforms.iOS'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: AndroidTv, light: AndroidTvLight },
        }),
        title: t('common:platforms.androidTv'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: FireTv, light: FireTvLight },
        }),
        title: t('common:platforms.fireTv'),
      },
    ],
    mobileCards: [
      {
        image: getImageByVariant({
          variant,
          images: { dark: Windows, light: WindowsLight },
        }),
        title: t('common:platforms.windows'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: MacOS, light: MacOSLight },
        }),
        title: t('common:platforms.macOS'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: Linux, light: LinuxLight },
        }),
        title: t('common:platforms.linux'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: Android, light: AndroidLight },
        }),
        title: t('common:platforms.android'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: iOS, light: iOSLight },
        }),
        title: t('common:platforms.iOS'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: AndroidTv, light: AndroidTvLight },
        }),
        title: t('common:platforms.androidTv'),
      },
      {
        image: getImageByVariant({
          variant,
          images: { dark: FireTv, light: FireTvLight },
        }),
        title: t('common:platforms.fireTv'),
      },
    ],
  }),
  countryList: ({ t }) => ({
    title: t('deals:countryList.title'),
    description: t('deals:countryList.description', { serverCount: SERVER_COUNT }),
    button: {
      link: PRICING_LINK,
      title: getCTAButtonText(t),
      buttonProps: {
        customEvent: { eventName: GA4_EVENTS.serversCtaClick },
      },
    },
    image: false,
  }),
  moneyBackGuarantee: (t) => ({
    data: [
      {
        image: getFetchedImage(images, 'deals/money-back-guarantee.png'),
        title: t('common:moneyBackGuarantee14.title'),
        description: t('common:moneyBackGuarantee14.description'),
      },
    ],
  }),
  footerCta: ({ discountPercentage, t }) => ({
    title: t('deals:footer.title'),
    description: discountPercentage
      ? t('deals:footer.descriptionWithDiscount', { discountPercentage })
      : null,
    reviews: getReviews({ t, images }),
  }),
});

export const getModalConfig = () => ({
  container: Modal.Container,
  content: DealsCouponErrorModal,
});
